/**
 * Sentiment Panel Component
 *
 * Displays sentiment analysis data with token tabs
 * Memoized for performance optimization
 */

import { memo } from 'react';
import { Icons } from '../Icons';
import { getSentimentColor } from '../../hooks/useTradingSignals';
import type { Token } from '../../types';

export interface SentimentData {
  source: string;
  sentiment: 'bullish' | 'neutral' | 'bearish';
  score: number;
  mentions: number;
  trend: 'up' | 'down' | 'stable';
}

interface SentimentPanelProps {
  tokens: Token[];
  selectedToken: string;
  onSelectToken: (tokenId: string) => void;
  sentimentData: SentimentData[];
  sentimentByToken: Record<string, SentimentData[]>;
  usingMockData: boolean;
}

/**
 * Sentiment Data Row
 */
const SentimentRow = memo(function SentimentRow({ data }: { data: SentimentData }) {
  return (
    <div
      style={{
        padding: '16px',
        background: 'var(--bg)',
        borderRadius: '12px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '4px' }}>{data.source}</div>
        <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
          {data.mentions > 0 && `${data.mentions.toLocaleString()} mentions`}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>Sentiment</div>
          <div
            style={{
              padding: '6px 14px',
              borderRadius: '8px',
              background: getSentimentColor(data.sentiment),
              color: '#fff',
              fontSize: '13px',
              fontWeight: 600,
              textTransform: 'capitalize'
            }}
          >
            {data.sentiment}
          </div>
        </div>

        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>Score</div>
          <div style={{ fontSize: '20px', fontWeight: 700, color: getSentimentColor(data.sentiment) }}>
            {data.score > 0 ? '+' : ''}{data.score}
          </div>
        </div>

        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>Trend</div>
          {data.trend === 'up' && <Icons.TrendingUp size={24} style={{ color: '#10b981' }} />}
          {data.trend === 'down' && <Icons.TrendingDown size={24} style={{ color: '#ef4444' }} />}
          {data.trend === 'stable' && <div style={{ fontSize: '18px' }}>→</div>}
        </div>
      </div>
    </div>
  );
});

/**
 * Token Tabs Component
 */
const TokenTabs = memo(function TokenTabs({
  tokens,
  selectedToken,
  onSelectToken,
  sentimentByToken
}: {
  tokens: Token[];
  selectedToken: string;
  onSelectToken: (tokenId: string) => void;
  sentimentByToken: Record<string, SentimentData[]>;
}) {
  return (
    <div style={{
      display: 'flex',
      gap: '8px',
      marginBottom: '16px',
      overflowX: 'auto',
      padding: '4px',
      background: 'var(--bg)',
      borderRadius: '12px'
    }}>
      {tokens.slice(0, 5).map((token) => {
        const isSelected = selectedToken === token.id || (!selectedToken && token === tokens[0]);
        const hasData = sentimentByToken[token.id];
        return (
          <button
            key={token.id}
            onClick={() => onSelectToken(token.id)}
            disabled={!hasData}
            style={{
              padding: '10px 16px',
              borderRadius: '8px',
              border: isSelected ? '2px solid var(--accent)' : '2px solid transparent',
              background: isSelected ? 'rgba(139, 92, 246, 0.15)' : 'transparent',
              color: hasData ? (isSelected ? 'var(--accent)' : 'var(--text)') : 'var(--muted)',
              fontSize: '13px',
              fontWeight: isSelected ? 600 : 500,
              cursor: hasData ? 'pointer' : 'not-allowed',
              opacity: hasData ? 1 : 0.5,
              transition: 'all 0.2s',
              whiteSpace: 'nowrap'
            }}
            title={hasData ? `View sentiment for ${token.displayName || token.id}` : 'Sentiment data not loaded'}
          >
            {token.displayName || token.id}
          </button>
        );
      })}
    </div>
  );
});

/**
 * Sentiment Panel Component
 */
export const SentimentPanel = memo(function SentimentPanel({
  tokens,
  selectedToken,
  onSelectToken,
  sentimentData,
  sentimentByToken,
  usingMockData
}: SentimentPanelProps) {
  return (
    <div className="card" style={{ marginBottom: '24px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Icons.Activity size={20} />
        Market Sentiment Analysis
      </h2>

      {/* Token Tabs */}
      {!usingMockData && tokens.length > 0 && (
        <TokenTabs
          tokens={tokens}
          selectedToken={selectedToken}
          onSelectToken={onSelectToken}
          sentimentByToken={sentimentByToken}
        />
      )}

      <div style={{ display: 'grid', gap: '12px' }}>
        {sentimentData.map((data, index) => (
          <SentimentRow key={index} data={data} />
        ))}
      </div>

      <div style={{
        marginTop: '16px',
        padding: '14px',
        background: 'rgba(59, 130, 246, 0.1)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        borderRadius: '8px',
        fontSize: '13px',
        color: 'var(--muted)'
      }}>
        <strong>💡 Note:</strong> Sentiment analysis aggregates data from social media (Twitter/X, Reddit),
        news sources, and technical indicators to provide a comprehensive market view.
        Data updated every 15 minutes.
      </div>
    </div>
  );
});

SentimentPanel.displayName = 'SentimentPanel';
